// ─────────────────────────────────────────────────────────────────────────────
// adventure_model.ts — Data model for Adventure Mode
//
// Bookmarks, environment, inventory, dialog, action scroll, and state.
// Pure types — no runtime logic.
// ─────────────────────────────────────────────────────────────────────────────

import type { PageId } from './notebook_model.js';
import type { FoldState } from './the_fold.js';

// ── Environment ──────────────────────────────────────────────────────────────

export interface AdventureEnv {
  /** 0–1 continuous.  0 = midnight, 0.25 = dawn, 0.5 = noon, 0.75 = dusk. */
  timeOfDay: number;
  weather: 'clear' | 'rain' | 'fog' | 'storm';
  /** Active status conditions (e.g. 'wounded', 'exhausted', 'inspired'). */
  status: string[];
  /** Nearby narrative elements (e.g. 'ring', 'laura', 'fragment'). */
  proximity: string[];
  /** 0–1, the Whole's attention level — structural stress on the page. */
  stress: number;
  /** Current map location ID. */
  location: string;
}

export function defaultEnv(): AdventureEnv {
  return {
    timeOfDay: 0.35,   // mid-morning
    weather: 'clear',
    status: [],
    proximity: [],
    stress: 0,
    location: 'journal',  // start at the journal desk
  };
}

// ── Map locations ────────────────────────────────────────────────────────────

export interface MapLocation {
  id: string;
  name: string;
  description: string;
  /** IDs of locations reachable from here. */
  connections: string[];
}

export function defaultLocations(): MapLocation[] {
  return [
    { id: 'journal', name: 'The Journal', description: 'Your desk. The book lies open.', connections: ['courtyard'] },
    { id: 'courtyard', name: 'Courtyard', description: 'Cobblestone yard outside the study.', connections: ['journal', 'library', 'gate'] },
    { id: 'library', name: 'Library', description: 'Dusty towers of shelves.', connections: ['courtyard'] },
    { id: 'gate', name: 'Gate', description: 'The boundary. Beyond here, territory shifts.', connections: ['courtyard'] },
  ];
}

// ── Action scroll blocks ─────────────────────────────────────────────────────

export type ActionBlockKind =
  | 'dialog'         // speech from a character
  | 'narration'      // environment / system description
  | 'image'          // illustration or scene
  | 'option-group'   // multiple choices, selected by gesture
  | 'interactive'    // drawable area — ink persists
  | 'separator';     // visual scene break

export interface ActionBlockOption {
  id: string;
  text: string;
  /** Set to true when the player circles / checks / fills this option. */
  selected: boolean;
}

export interface ActionBlock {
  id: string;
  kind: ActionBlockKind;
  /** Adventure clockMs when this block appeared. */
  appearedAt: number;

  // ── Content fields (populated per kind) ──
  speaker?: DialogSpeaker;
  text?: string;
  options?: ActionBlockOption[];
  imageRef?: string;

  // ── Timer ──
  /** Countdown in ms. 0 = no timer (waits indefinitely or is instant). */
  timerMs: number;
  /** True once the player clicks the clock icon to skip. */
  skipped: boolean;
  /** True once the block has been interacted with / timed out. */
  resolved: boolean;

  // ── Ink ──
  /** Whether strokes drawn over this block persist (true for interactive/image). */
  inkPersist: boolean;

  // ── Layout (set by renderer, not serialised) ──
  /** Rendered height in canvas units — computed at draw time. */
  height: number;
}

/** Create an ActionBlock with sensible defaults. */
export function makeActionBlock(
  id: string,
  kind: ActionBlockKind,
  clockMs: number,
  overrides?: Partial<ActionBlock>,
): ActionBlock {
  return {
    id,
    kind,
    appearedAt: clockMs,
    timerMs: 0,
    skipped: false,
    resolved: false,
    inkPersist: kind === 'interactive' || kind === 'image',
    height: 0,
    ...overrides,
  };
}

// ── Bookmarks (the signature object) ─────────────────────────────────────────

export type SigilPosition = 'crown' | 'threshold' | 'foundation' | 'root';

export interface SigilSlot {
  position: SigilPosition;
  /** ID of the arcane assigned to this slot; undefined = empty. */
  arcaneId?: string;
  /** 0–1, charge held in this slot. */
  charge: number;
}

export function emptySigils(): [SigilSlot, SigilSlot, SigilSlot, SigilSlot] {
  return [
    { position: 'crown',      charge: 0 },
    { position: 'threshold',  charge: 0 },
    { position: 'foundation', charge: 0 },
    { position: 'root',       charge: 0 },
  ];
}

export type BookmarkState = 'dormant' | 'active' | 'spent' | 'corrupted';

export interface AdventureBookmark {
  id: string;
  name: string;
  sigils: [SigilSlot, SigilSlot, SigilSlot, SigilSlot];
  state: BookmarkState;
  /** Pages unlocked when this bookmark is inserted. */
  chapterPageIds: PageId[];
  /** Normalised polyline [x0,y0,x1,y1,...] for glyph matching. */
  glyphRef: number[];
  /** Accumulated narrative summary (grows as player explores the chapter). */
  narrative: string;
}

// ── Sub-tabs ─────────────────────────────────────────────────────────────────

export type AdventureSubTab =
  | 'action'
  | 'bookmarks'
  | 'character'
  | 'inventory'
  | 'history'
  | 'build';

export const ADVENTURE_SUB_TABS: readonly AdventureSubTab[] = [
  'action', 'bookmarks', 'character', 'inventory', 'history', 'build',
] as const;

// ── Dialog log ───────────────────────────────────────────────────────────────

export type DialogSpeaker = 'ireth' | 'laura' | 'system';

export interface DialogEntry {
  speaker: DialogSpeaker;
  text: string;
  timestamp: number;
  /** Which page the dialog appeared on (for back-reference). */
  pageId?: string;
}

// ── Top-level adventure state ────────────────────────────────────────────────

export interface AdventureState {
  /** False until the prologue is completed and adventure unlocks. */
  unlocked: boolean;
  env: AdventureEnv;
  bookmarks: AdventureBookmark[];
  currentSubTab: AdventureSubTab;
  /** Number of inventory pages the player owns (starts at 1). */
  inventoryPageCount: number;
  /** IDs of arcanes the player has learned. */
  knownArcanes: string[];
  dialogLog: DialogEntry[];
  /** Cumulative adventure time in milliseconds. */
  clockMs: number;

  // ── The Fold ──
  /** House state — undefined until the adventure section unlocks. */
  fold?: FoldState;

  // ── Action scroll ──
  /** Blocks in the action scroll, newest at the end. */
  actionBlocks: ActionBlock[];

  // ── Map ──
  /** Known locations the player has discovered. */
  locations: MapLocation[];
}

export function defaultAdventureState(): AdventureState {
  return {
    unlocked: false,
    env: defaultEnv(),
    bookmarks: [],
    currentSubTab: 'action',
    inventoryPageCount: 1,
    knownArcanes: [],
    dialogLog: [],
    clockMs: 0,
    actionBlocks: [],
    locations: defaultLocations(),
  };
}
